import { useEffect, useMemo, useState } from "react";
import { admin, type Gap, type GapRows } from "../../api/admin";

const PAGE = 100;

/**
 * What the catalogue is missing.
 *
 * Every other page in the panel is about what ran. This one is about what did
 * not: the releases nobody has looked up, the matched releases Bandcamp gave no
 * tags for, the tracks nobody has listened to. None of those produce a log line
 * of their own, because no run ever touched them -- which is exactly why they
 * were invisible until now.
 *
 * A count with no remedy beside it is only something to feel bad about, so
 * every finding names the job that fills it and offers the button.
 */
export function Gaps({ onWatch }: { onWatch: (id: number) => void }) {
  const [found, setFound] = useState<Gap[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => admin.gaps().then(setFound)
    .catch((e: Error) => setError(e.message));
  useEffect(() => { load(); }, []);

  if (error) return <div className="error">{error}</div>;
  if (!found) return <div className="loading"><span className="spinner" />counting…</div>;

  const work = found.filter((g) => !g.terminal);
  const findings = found.filter((g) => g.terminal);
  const outstanding = work.reduce((n, g) => n + g.count, 0);

  return (
    <>
      <section className="panel-section">
        <h2>Still to do <span className="dim">({outstanding.toLocaleString()} rows)</span></h2>
        <p className="dim small">
          Each of these is filled by running the job named beside it. The same
          nine questions are written to a run log, dated and kept, by the{" "}
          <code>audit</code> job.
        </p>
        <div className="gaps">
          {work.map((g) => (
            <GapCard key={g.category} gap={g} open={open === g.category}
                     onToggle={() => setOpen(open === g.category ? null : g.category)}
                     onRan={(id) => { onWatch(id); }} onDone={load} />
          ))}
        </div>
      </section>

      <section className="panel-section">
        <h2>Findings</h2>
        <p className="dim small">
          Not a backlog: nothing will fill these. A release Bandcamp has no tags
          for and a track it will not stream are answers, and they are here so
          that a missing chip or a missing tempo can be explained rather than
          wondered about.
        </p>
        <div className="gaps">
          {findings.map((g) => (
            <GapCard key={g.category} gap={g} open={open === g.category}
                     onToggle={() => setOpen(open === g.category ? null : g.category)}
                     onRan={onWatch} onDone={load} />
          ))}
        </div>
      </section>
    </>
  );
}

function GapCard({ gap, open, onToggle, onRan, onDone }: {
  gap: Gap;
  open: boolean;
  onToggle: () => void;
  onRan: (runId: number) => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = () => {
    if (!gap.fix) return;
    setBusy(true);
    admin.run(gap.fix, {})
      .then((r) => { onRan(r.runId); onDone(); })
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(false));
  };

  return (
    <div className={`gap${open ? " open" : ""}${gap.count === 0 ? " clear" : ""}`}>
      <header>
        <button className="gaphd" onClick={onToggle} aria-expanded={open}>
          <b className="num">{gap.count.toLocaleString()}</b>
          <span className="t">{gap.title}</span>
          <code className={`cat ${gap.terminal ? "info" : "warn"}`}>{gap.category}</code>
        </button>
        {gap.fix && gap.count > 0 && (
          <button className="primary" disabled={busy} onClick={run}>
            {busy ? "starting…" : `Run ${gap.fix}`}
          </button>
        )}
      </header>
      <p className="summary">{gap.detail}</p>
      {error && <div className="error">{error}</div>}
      {open && <GapList category={gap.category} subject={gap.subject} />}
    </div>
  );
}

/** The rows behind one finding, searchable and paged. */
function GapList({ category, subject }: { category: string; subject: "release" | "track" }) {
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [res, setRes] = useState<GapRows | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debounced = useDebounced(q, 300);
  const args = useMemo(() => ({ q: debounced || undefined, limit: PAGE, offset }),
    [debounced, offset]);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    admin.gapRows(category, args)
      .then((r) => { if (alive) { setRes(r); setError(null); } })
      .catch((e: Error) => alive && setError(e.message))
      .finally(() => alive && setBusy(false));
    return () => { alive = false; };
  }, [category, args]);

  return (
    <div className="gaprows">
      <div className="controls">
        <input
          type="search"
          placeholder="Find an artist, a title, a label, a shop"
          value={q}
          onChange={(e) => { setOffset(0); setQ(e.target.value); }}
        />
        <span className="count">
          {busy && <span className="spinner" />}
          {res && `${res.total.toLocaleString()} in all`}
        </span>
      </div>

      {error && <div className="error">{error}</div>}
      {res?.rows.length === 0 && !busy && <div className="empty">Nothing matches that.</div>}

      {res && res.rows.length > 0 && (
        <table className="runs">
          <thead>
            <tr>
              <th>release</th>
              {subject === "track" && <th>track</th>}
              <th>label</th><th>date</th><th>shops</th><th>why</th>
            </tr>
          </thead>
          <tbody>
            {res.rows.map((r) => (
              <tr key={`${r.key}:${r.pos ?? ""}`}>
                <td>
                  {r.url
                    ? <a href={r.url} target="_blank" rel="noreferrer">{r.artist} — {r.title}</a>
                    : <>{r.artist} — {r.title}</>}
                  <div className="dim small mono">{r.key}</div>
                </td>
                {subject === "track" && (
                  <td className="dim small">{r.pos}. {r.track || <em>untitled</em>}</td>
                )}
                <td className="dim small">{r.label}</td>
                <td className="dim small nowrap">{r.date || "—"}</td>
                <td className="dim small">{r.shops?.join(", ")}</td>
                <td className="dim small">{r.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {res && (offset > 0 || res.rows.length === PAGE) && (
        <div className="more">
          <button disabled={offset === 0 || busy}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}>← back</button>
          <span className="dim small pager">
            {(offset + 1).toLocaleString()}–{(offset + res.rows.length).toLocaleString()}
            {" of "}{res.total.toLocaleString()}
          </span>
          <button disabled={res.rows.length < PAGE || busy}
                  onClick={() => setOffset(offset + PAGE)}>more →</button>
        </div>
      )}
    </div>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
